import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
    useTask,
    useTaskComments,
    useAddTaskComment,
    useAddSubtask,
    useUpdateSubtask,
    useDeleteSubtask,
    useDeleteTask,
    useUsers,
} from '../hooks/useApi';
import TaskModal from '../components/TaskModal';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    ConfirmDialog,
    DetailRow,
    EmptyState,
    ErrorState,
    ListSkeleton,
    PageHeader,
    PriorityBadge,
    ProgressBar,
    Skeleton,
    StatusBadge,
} from '../components/ui';

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
        : 'Not set';

const TaskDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { data: task, isLoading, isError } = useTask(id);

    const adminRoles = ['Manager', 'Head of Department', 'Developer'];
    const isGlobalAdmin = user && adminRoles.includes(user.role);
    const { data: comments, isLoading: commentsLoading } = useTaskComments(id);
    const addCommentMutation = useAddTaskComment();
    const addSubtaskMutation = useAddSubtask();
    const updateSubtaskMutation = useUpdateSubtask();
    const deleteSubtaskMutation = useDeleteSubtask();
    const deleteTask = useDeleteTask();

    const [commentText, setCommentText] = useState('');
    const [subtaskTitle, setSubtaskTitle] = useState('');
    const [subtaskAssignee, setSubtaskAssignee] = useState('');
    const { data: userResult } = useUsers();
    const users = userResult?.data || [];
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'Delete',
        onConfirm: () => { },
    });

    const closeConfirm = () => setConfirmConfig((prev) => ({ ...prev, isOpen: false }));

    const handleAddComment = (e) => {
        e.preventDefault();
        if (!commentText.trim()) return;
        addCommentMutation.mutate(
            { id, comment: commentText },
            { onSuccess: () => setCommentText('') }
        );
    };

    const handleAddSubtask = (e) => {
        e.preventDefault();
        if (!subtaskTitle.trim()) return;
        addSubtaskMutation.mutate(
            { id, data: { title: subtaskTitle, assignee_id: subtaskAssignee || null } },
            {
                onSuccess: () => {
                    setSubtaskTitle('');
                    setSubtaskAssignee('');
                },
            }
        );
    };

    const toggleSubtask = (subtask) => {
        updateSubtaskMutation.mutate({
            taskId: id,
            subtaskId: subtask.id,
            data: { is_completed: !subtask.is_completed },
        });
    };

    const handleDeleteSubtask = (subtaskId) => {
        setConfirmConfig({
            isOpen: true,
            title: 'Delete checklist item',
            message: 'This removes the item from the checklist. This cannot be undone.',
            confirmText: 'Delete item',
            onConfirm: () => {
                deleteSubtaskMutation.mutate({ taskId: id, subtaskId });
                closeConfirm();
            },
        });
    };

    const handleDeleteTask = () => {
        setConfirmConfig({
            isOpen: true,
            title: 'Delete task',
            message: 'This permanently removes the task, its checklist and its comments.',
            confirmText: 'Delete task',
            onConfirm: async () => {
                try {
                    await deleteTask.mutateAsync(id);
                    closeConfirm();
                    navigate('/tasks');
                } catch (error) {
                    console.error('Error deleting task:', error);
                }
            },
        });
    };

    if (isLoading) {
        return (
            <>
                <Skeleton w={220} h={26} className="mb-5" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Card className="lg:col-span-2">
                        <CardBody>
                            <ListSkeleton rows={5} />
                        </CardBody>
                    </Card>
                    <Card>
                        <CardBody>
                            <ListSkeleton rows={4} />
                        </CardBody>
                    </Card>
                </div>
            </>
        );
    }

    if (isError || !task) {
        return (
            <Card>
                <ErrorState
                    title="Task not found"
                    description="It may have been deleted, or you may not have access to it."
                    onRetry={() => navigate('/tasks')}
                />
            </Card>
        );
    }

    const subtasks = task.subtasks || [];
    const doneCount = subtasks.filter((s) => s.is_completed).length;

    return (
        <>
            <PageHeader
                title={task.title}
                breadcrumbs={[
                    { label: 'Tasks', to: '/tasks' },
                    { label: task.task_code || 'Task' },
                ]}
                actions={
                    <>
                        <Button icon={Pencil} onClick={() => setIsEditModalOpen(true)}>
                            Edit
                        </Button>
                        {isGlobalAdmin && (
                            <Button variant="danger" icon={Trash2} onClick={handleDeleteTask}>
                                Delete
                            </Button>
                        )}
                    </>
                }
            >
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <StatusBadge status={task.status} />
                    <PriorityBadge priority={task.priority} />
                    {task.task_code && (
                        <span className="text-label font-mono text-ink-500">{task.task_code}</span>
                    )}
                    {task.project_name && (
                        <>
                            <span className="text-ink-300" aria-hidden="true">·</span>
                            <Link
                                to={`/projects/${task.project_id}`}
                                className="text-label text-secondary-600 hover:text-secondary-700 transition-colors duration-150"
                            >
                                {task.project_name}
                            </Link>
                        </>
                    )}
                </div>
            </PageHeader>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    {/* Description */}
                    <Card>
                        <CardHeader title="Description" />
                        <CardBody>
                            {task.description ? (
                                <p className="text-md text-ink-700 whitespace-pre-line leading-relaxed max-w-[75ch]">
                                    {task.description}
                                </p>
                            ) : (
                                <p className="text-base text-ink-500">No description provided.</p>
                            )}
                        </CardBody>
                    </Card>

                    {/* Checklist */}
                    <Card>
                        <CardHeader
                            title="Checklist"
                            subtitle={subtasks.length ? `${doneCount} of ${subtasks.length} complete` : undefined}
                        />

                        {subtasks.length > 0 && (
                            <ul className="divide-y divide-[var(--divider-color)]">
                                {subtasks.map((subtask) => (
                                    <li key={subtask.id} className="group flex items-center gap-3 px-5 py-2.5">
                                        <input
                                            type="checkbox"
                                            id={`subtask-${subtask.id}`}
                                            checked={subtask.is_completed}
                                            onChange={() => toggleSubtask(subtask)}
                                            className="w-3.5 h-3.5 accent-[var(--primary)] shrink-0"
                                        />
                                        <label
                                            htmlFor={`subtask-${subtask.id}`}
                                            className={`text-base cursor-pointer flex-1 min-w-0 ${subtask.is_completed
                                                ? 'text-ink-400 line-through'
                                                : 'text-ink-800'
                                                }`}
                                        >
                                            {subtask.title}
                                        </label>

                                        {subtask.assignee_name && (
                                            <span className="flex items-center gap-1.5 shrink-0">
                                                <Avatar name={subtask.assignee_name} size="xs" />
                                                <span className="text-micro text-ink-500 hidden sm:inline">
                                                    {subtask.assignee_name}
                                                </span>
                                            </span>
                                        )}

                                        {isGlobalAdmin && (
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteSubtask(subtask.id)}
                                                aria-label={`Delete ${subtask.title}`}
                                                className="shrink-0 p-1 rounded text-ink-400 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-danger hover:bg-danger-subtle transition-all duration-150"
                                            >
                                                <Trash2 size={14} aria-hidden="true" />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="px-5 py-3 border-t border-[var(--divider-color)]">
                            <form onSubmit={handleAddSubtask} className="flex flex-wrap items-center gap-2">
                                <input
                                    type="text"
                                    value={subtaskTitle}
                                    onChange={(e) => setSubtaskTitle(e.target.value)}
                                    placeholder="Add a checklist item…"
                                    aria-label="Checklist item"
                                    className="input flex-1 min-w-[180px]"
                                    required
                                />
                                <select
                                    value={subtaskAssignee}
                                    onChange={(e) => setSubtaskAssignee(e.target.value)}
                                    aria-label="Assign checklist item"
                                    className="input w-auto"
                                >
                                    <option value="">Unassigned</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </option>
                                    ))}
                                </select>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    icon={Plus}
                                    loading={addSubtaskMutation.isPending}
                                >
                                    Add
                                </Button>
                            </form>
                        </div>
                    </Card>

                    {/* Comments */}
                    <Card>
                        <CardHeader title="Comments" subtitle={comments?.length ? `${comments.length}` : undefined} />
                        <CardBody>
                            <form onSubmit={handleAddComment} className="mb-5">
                                <label htmlFor="task-comment" className="sr-only">
                                    Add a comment
                                </label>
                                <textarea
                                    id="task-comment"
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    className="input textarea"
                                    rows={3}
                                    placeholder="Add a comment…"
                                    required
                                />
                                <div className="flex justify-end mt-2">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        size="sm"
                                        loading={addCommentMutation.isPending}
                                    >
                                        Post comment
                                    </Button>
                                </div>
                            </form>

                            {commentsLoading ? (
                                <ListSkeleton rows={3} />
                            ) : comments?.length ? (
                                <ol className="space-y-4">
                                    {comments.map((comment) => (
                                        <li key={comment.id} className="flex gap-2.5">
                                            <Avatar name={comment.user_name} size="md" />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-baseline gap-2 flex-wrap">
                                                    <span className="text-base font-medium text-ink-900">
                                                        {comment.user_name}
                                                    </span>
                                                    <span className="text-micro text-ink-400 tabular">
                                                        {new Date(comment.created_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="text-base text-ink-700 whitespace-pre-wrap mt-0.5 leading-relaxed">
                                                    {comment.comment}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <EmptyState
                                    compact
                                    title="No comments yet"
                                    description="Start the discussion by posting the first comment."
                                />
                            )}
                        </CardBody>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                    <Card>
                        <CardHeader title="Details" />
                        <CardBody className="divide-y divide-[var(--divider-color)] py-2">
                            <DetailRow label="Assignee">
                                {task.assignee_name ? (
                                    <span className="flex items-center gap-2">
                                        <Avatar name={task.assignee_name} size="sm" />
                                        {task.assignee_name}
                                    </span>
                                ) : (
                                    <span className="text-muted">Unassigned</span>
                                )}
                            </DetailRow>
                            <DetailRow label="Priority">
                                <PriorityBadge priority={task.priority} />
                            </DetailRow>
                            <DetailRow label="Created by">{task.created_by_name}</DetailRow>
                            <DetailRow label="Start date">
                                <span className="tabular">{formatDate(task.start_date)}</span>
                            </DetailRow>
                            <DetailRow label="Due date">
                                <span className="tabular">{formatDate(task.due_date)}</span>
                            </DetailRow>
                            <DetailRow label="Estimated">
                                <span className="tabular">{task.estimated_hours || 0} h</span>
                            </DetailRow>
                            <DetailRow label="Actual">
                                <span className="tabular">{task.actual_hours || 0} h</span>
                            </DetailRow>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader title="Completion" />
                        <CardBody>
                            <ProgressBar value={task.progress} showValue />
                        </CardBody>
                    </Card>

                    {task.tags && (
                        <Card>
                            <CardHeader title="Tags" />
                            <CardBody>
                                <div className="flex flex-wrap gap-1.5">
                                    {task.tags
                                        .split(',')
                                        .map((t) => t.trim())
                                        .filter(Boolean)
                                        .map((tag) => (
                                            <Badge key={tag} variant="neutral">
                                                {tag}
                                            </Badge>
                                        ))}
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </div>
            </div>

            {isEditModalOpen && <TaskModal task={task} onClose={() => setIsEditModalOpen(false)} />}

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                title={confirmConfig.title}
                message={confirmConfig.message}
                confirmText={confirmConfig.confirmText}
                variant="danger"
                onConfirm={confirmConfig.onConfirm}
                onClose={closeConfirm}
            />
        </>
    );
};

export default TaskDetail;
